import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Translator } from '@sharedV11/services/translator/translator.service';
import { AccessControlTipoControl } from '@sharedV11/types/access-control-kuroi.model';

interface CalItemFecha {
  FechaAcceso: string;
  FechaAccesoFin: string;
  pkId?: string;
  CodigoTipoControl?: string;
}

interface CalItemHora {
  FechaAcceso: string;
  FechaAccesoFin: string;
  pkId?: string;
  CodigoTipoControl?: string;
}

@Component({
  selector: 'app-access-control-tipo-control-dialog',
  templateUrl: './access-control-tipo-control-dialog.component.html',
  styleUrls: ['./access-control-tipo-control-dialog.component.scss'],
})
export class AccessControlTipoControlDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tipo: AccessControlTipoControl | null = null;
  @Input() translation: any;
  @Output() saveRequested = new EventEmitter<AccessControlTipoControl>();
  @Output() cancelRequested = new EventEmitter<void>();

  form!: FormGroup;

  panelCalendarioEnabled = false;
  panelCalendarioHorarioEnabled = false;
  calendarioPanelExpanded = true;
  calendarioHorarioPanelExpanded = true;

  calendarioDS = new MatTableDataSource<CalItemFecha>([]);
  calendarioHorarioDS = new MatTableDataSource<CalItemHora>([]);
  columnasCalendario = ['Acciones', 'FechaInicio', 'FechaFin'];
  columnasCalendarioHorario = ['Acciones', 'HoraInicio', 'HoraFin'];
  calendarioRequiredError = false;
  calendarioHorarioRequiredError = false;

  nivelesEntrada = [
    { id: '1', label: 'Normal' },
    { id: '2', label: 'Reducida' },
    { id: '3', label: 'Gratuita' },
    { id: '4', label: 'Normal ida y vuelta' },
    { id: '5', label: 'Reducida ida y vuelta' },
    { id: '6', label: 'Gratuita ida y vuelta' },
  ];

  diasSemana = [
    { id: '1', value: 'Lunes' },
    { id: '2', value: 'Martes' },
    { id: '3', value: 'Miércoles' },
    { id: '4', value: 'Jueves' },
    { id: '5', value: 'Viernes' },
    { id: '6', value: 'Sábado' },
    { id: '7', value: 'Domingo' },
  ];

  t: any = {};
  private readonly integerValidators = [Validators.pattern(/^-?\d+$/)];
  private readonly positiveValidators = [Validators.pattern(/^\d+$/), Validators.min(1)];
  private destroy$ = new Subject<void>();

  constructor(private fb: FormBuilder, private translator: Translator) {}

  ngOnInit(): void {
    this.t = this.translation ?? this.translator?.GetTranslations?.() ?? {};
    this.buildForm();
    this.hookValueChanges();
    if (this.tipo) {
      this.patchFromInput(this.tipo);
    }
    this.applyComprobacionesState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['translation'] && !changes['translation'].firstChange) {
      this.t = changes['translation'].currentValue ?? this.translator?.GetTranslations?.() ?? {};
    }
    if (changes['tipo'] && !changes['tipo'].firstChange && this.form) {
      this.patchFromInput(changes['tipo'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  label(key: string, fallback?: string): string {
    const v = this.t?.[key];
    return v !== undefined && v !== null && v !== '' ? v : fallback ?? key;
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  /**
   * Retorna true se deve mostrar o erro de Días Semana:
   * - CompDiaSemana está ativo
   * - Nenhum dia está selecionado
   */
  get shouldShowDiasSemanaError(): boolean {
    return this.form.value.CompDiaSemana && !(this.f['DiasSemana'].value || []).length;
  }

  hasDia(dia: string): boolean {
    const value: string[] = this.f['DiasSemana'].value || [];
    return value.includes(dia);
  }

  onDiaToggle(dia: string, checked: boolean): void {
    const value: string[] = [...(this.f['DiasSemana'].value || [])];
    const index = value.indexOf(dia);
    if (checked && index === -1) {
      value.push(dia);
    } else if (!checked && index !== -1) {
      value.splice(index, 1);
    }
    this.f['DiasSemana'].setValue(value);
  }

  addCalendarioRow(): void {
    if (!this.panelCalendarioEnabled || this.hasIncompleteRow(this.calendarioDS.data)) {
      return;
    }
    this.calendarioDS.data = [
      ...this.calendarioDS.data,
      {
        FechaAcceso: '',
        FechaAccesoFin: '',
        CodigoTipoControl: this.tipo?.CodigoTipoControl ?? '',
      },
    ];
    this.calendarioPanelExpanded = true;
    this.calendarioRequiredError = false;
  }

  removeCalendarioRow(index: number): void {
    const data = [...this.calendarioDS.data];
    if (!data[index]) {
      return;
    }
    data.splice(index, 1);
    this.calendarioDS.data = data;
    if (!this.calendarioDS.data.length) {
      this.calendarioPanelExpanded = true;
  }
  }

  trackCal(index: number, row: CalItemFecha): string {
    return `${index}-${row.FechaAcceso}-${row.FechaAccesoFin}`;
  }

  addCalendarioHorarioRow(): void {
    if (!this.panelCalendarioHorarioEnabled || this.hasIncompleteRow(this.calendarioHorarioDS.data)) {
      return;
    }
    this.calendarioHorarioDS.data = [
      ...this.calendarioHorarioDS.data,
      {
        FechaAcceso: '',
        FechaAccesoFin: '',
        CodigoTipoControl: this.tipo?.CodigoTipoControl ?? '',
      },
    ];
    this.calendarioHorarioPanelExpanded = true;
    this.calendarioHorarioRequiredError = false;
    }

  removeCalendarioHorarioRow(index: number): void {
    const data = [...this.calendarioHorarioDS.data];
    if (!data[index]) {
      return;
    }
    data.splice(index, 1);
    this.calendarioHorarioDS.data = data;
    if (!this.calendarioHorarioDS.data.length) {
      this.calendarioHorarioPanelExpanded = true;
  }
  }

  trackCalH(index: number, row: CalItemHora): string {
    return `${index}-${row.FechaAcceso}-${row.FechaAccesoFin}`;
  }

  togglePanel(panel: MatExpansionPanel, type: 'calendario' | 'horario', event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    if (!panel) {
      return;
    }
    panel.toggle();
    if (type === 'calendario') {
      this.calendarioPanelExpanded = panel.expanded;
    } else {
      this.calendarioHorarioPanelExpanded = panel.expanded;
    }
  }

  onSave(): void {
    this.form.markAllAsTouched();
    this.calendarioRequiredError = false;
    this.calendarioHorarioRequiredError = false;

    if (this.form.invalid) {
      return;
    }

    if (this.f['CompDiaSemana'].value && !(this.f['DiasSemana'].value || []).length) {
      return;
    }

    // COMENTADO TEMPORARIAMENTE - SERÁ IMPLEMENTADO NO FUTURO
    // if (this.panelCalendarioEnabled && !this.calendarioDS.data.length) {
    //   this.calendarioRequiredError = true;
    //   return;
    // }

    // COMENTADO TEMPORARIAMENTE - SERÁ IMPLEMENTADO NO FUTURO
    // if (this.panelCalendarioHorarioEnabled && !this.calendarioHorarioDS.data.length) {
    //   this.calendarioHorarioRequiredError = true;
    //   return;
    // }

    if (this.panelCalendarioEnabled && !this.validateFechas(this.calendarioDS.data)) {
      return;
    }

    if (this.panelCalendarioHorarioEnabled && !this.validateHoras(this.calendarioHorarioDS.data)) {
      return;
    }

    const raw = this.form.getRawValue();

    const updated: AccessControlTipoControl = {
      ...this.tipo,
      CodigoTipoControl: raw.Codigo,
      Nombre: raw.Nombre,
      Descripcion: raw.Descripcion ?? '',
      NivelEntrada: raw.NivelEntrada,
      CompHoraria: !!raw.CompHoraria,
      CompDiaria: !!raw.CompDiaria,
      CompIntervalo: !!raw.CompIntervalo,
      CompDiaSemana: !!raw.CompDiaSemana,
      CompPasoPrevio: !!raw.CompPasoPrevio,
      CompCalendario: !!raw.CompCalendario,
      CompCalendHorario: !!raw.CompCalendHorario,
      DenegarAcceso: !!raw.DenegarAcceso,
      PermisoSinFecha: !!raw.PermisoSinFecha,
      DiasIntervaloDesde: raw.DiasIntervaloDesde ?? '',
      DiasIntervalo: raw.DiasIntervalo ?? '',
      NumPasosPrevios: parseInt(raw.NumPasosPrevios || '1', 10),
      NumPasosPreviosDiarios: parseInt(raw.NumPasosPreviosDiarios || '1', 10),
      AccesoLunes: this.hasDia('1'),
      AccesoMartes: this.hasDia('2'),
      AccesoMiercoles: this.hasDia('3'),
      AccesoJueves: this.hasDia('4'),
      AccesoViernes: this.hasDia('5'),
      AccesoSabado: this.hasDia('6'),
      AccesoDomingo: this.hasDia('7'),
      ListadoTiposControlCalendario: this.panelCalendarioEnabled
        ? this.calendarioDS.data.map(item => ({
            pkId: item.pkId,
            CodigoTipoControl: item.CodigoTipoControl ?? updated.CodigoTipoControl,
            FechaAcceso: this.fromDateInputFormat(item.FechaAcceso),
            FechaAccesoFin: this.fromDateInputFormat(item.FechaAccesoFin),
          }))
        : [],
      ListadoTiposControlCalendarioHorario: this.panelCalendarioHorarioEnabled
        ? this.calendarioHorarioDS.data.map(item => ({
            pkId: item.pkId,
            CodigoTipoControl: item.CodigoTipoControl ?? updated.CodigoTipoControl,
            FechaAcceso: this.fromTimeInputFormat(item.FechaAcceso),
            FechaAccesoFin: this.fromTimeInputFormat(item.FechaAccesoFin),
          }))
        : [],
    } as AccessControlTipoControl;

    this.saveRequested.emit(updated);
  }

  onCancel(): void {
    this.cancelRequested.emit();
  }

  private buildForm(): void {
    this.form = this.fb.group({
      Codigo: [{ value: this.tipo?.CodigoTipoControl ?? '', disabled: true }, [Validators.required]],
      Nombre: ['', [Validators.required, Validators.pattern(/^[A-Za-zÀ-ÿ0-9 .\\-]+$/)]],
      Descripcion: ['', [Validators.pattern(/^[A-Za-zÀ-ÿ0-9 .\\-]*$/)]],
      NivelEntrada: ['1', [Validators.required]],
      CompHoraria: [false],
      CompDiaria: [false],
      CompIntervalo: [false],
      CompDiaSemana: [false],
      CompPasoPrevio: [false],
      CompCalendario: [false],
      CompCalendHorario: [false],
      DenegarAcceso: [false],
      PermisoSinFecha: [false],
      DiasIntervaloDesde: [''],
      DiasIntervalo: [''],
      NumPasosPrevios: ['1'],
      NumPasosPreviosDiarios: ['1'],
      DiasSemana: [[]],
    });
  }

  private hookValueChanges(): void {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyComprobacionesState();
    });
        }

  private patchFromInput(item: AccessControlTipoControl | null): void {
    if (!item) {
        return;
      }

    const dias: string[] = [];
    if (truthy(item.AccesoLunes)) dias.push('1');
    if (truthy(item.AccesoMartes)) dias.push('2');
    if (truthy(item.AccesoMiercoles)) dias.push('3');
    if (truthy(item.AccesoJueves)) dias.push('4');
    if (truthy(item.AccesoViernes)) dias.push('5');
    if (truthy(item.AccesoSabado)) dias.push('6');
    if (truthy(item.AccesoDomingo)) dias.push('7');

    this.form.patchValue(
      {
        Codigo: item.CodigoTipoControl ?? '',
        Nombre: item.Nombre ?? '',
        Descripcion: item.Descripcion ?? '',
        NivelEntrada: (item.NivelEntrada ?? '1').toString(),
        CompHoraria: truthy(item.CompHoraria),
        CompDiaria: truthy(item.CompDiaria),
        CompIntervalo: truthy(item.CompIntervalo),
        CompDiaSemana: truthy(item.CompDiaSemana),
        CompPasoPrevio: truthy(item.CompPasoPrevio),
        CompCalendario: truthy(item.CompCalendario),
        CompCalendHorario: truthy(item.CompCalendHorario),
        DenegarAcceso: truthy(item.DenegarAcceso),
        PermisoSinFecha: truthy(item.PermisoSinFecha),
        DiasIntervaloDesde: valueOrEmpty(item.DiasIntervaloDesde),
        DiasIntervalo: valueOrEmpty(item.DiasIntervalo),
        NumPasosPrevios: valueOrEmpty(item.NumPasosPrevios) || '1',
        NumPasosPreviosDiarios: valueOrEmpty(item.NumPasosPreviosDiarios) || '1',
        DiasSemana: dias,
      },
      { emitEvent: false }
    );

    // COMENTADO TEMPORARIAMENTE - SERÁ IMPLEMENTADO NO FUTURO
    // this.panelCalendarioEnabled = truthy(item.CompCalendario);
    // this.panelCalendarioHorarioEnabled = truthy(item.CompCalendHorario);
    
    // Forçar como false enquanto não implementado
    this.panelCalendarioEnabled = false;
    this.panelCalendarioHorarioEnabled = false;

    const calendario = (item as any).ListadoTiposControlCalendario ?? [];
    const calendarioHorario = (item as any).ListadoTiposControlCalendarioHorario ?? [];

    this.calendarioDS.data = calendario.map((c: any) => ({
      FechaAcceso: this.toDateInputFormat(c.FechaAcceso),
      FechaAccesoFin: this.toDateInputFormat(c.FechaAccesoFin),
      pkId: c.pkId ?? c.PkId ?? c.pkID ?? c.pkid ?? undefined,
      CodigoTipoControl: c.CodigoTipoControl ?? this.tipo?.CodigoTipoControl ?? '',
    }));

    this.calendarioHorarioDS.data = calendarioHorario.map((c: any) => {
      const rawInicio = c.HoraAcceso ?? c.FechaAcceso ?? '';
      const rawFin = c.HoraAccesoFin ?? c.FechaAccesoFin ?? '';

      return {
        FechaAcceso: this.toTimeInputFormat(rawInicio),
        FechaAccesoFin: this.toTimeInputFormat(rawFin),
        pkId: c.pkId ?? c.PkId ?? c.pkID ?? c.pkid ?? undefined,
        CodigoTipoControl: c.CodigoTipoControl ?? this.tipo?.CodigoTipoControl ?? '',
      };
    });

    this.applyComprobacionesState();
  }

  private applyComprobacionesState(): void {
    const value = this.form?.getRawValue?.() ?? {};

    const intervalo = !!value.CompIntervalo;
    toggleEnable(this.f['DiasIntervaloDesde'], intervalo);
    toggleEnable(this.f['DiasIntervalo'], intervalo);
    this.setControlValidators('DiasIntervaloDesde', this.integerValidators, intervalo);
    this.setControlValidators('DiasIntervalo', this.integerValidators, intervalo);
    if (!intervalo) {
      this.f['DiasIntervaloDesde'].setValue('', { emitEvent: false });
      this.f['DiasIntervalo'].setValue('', { emitEvent: false });
    }

    const pasoPrevio = !!value.CompPasoPrevio;
    toggleEnable(this.f['NumPasosPrevios'], pasoPrevio);
    toggleEnable(this.f['NumPasosPreviosDiarios'], pasoPrevio);
    this.setControlValidators('NumPasosPrevios', this.positiveValidators, pasoPrevio);
    this.setControlValidators('NumPasosPreviosDiarios', this.positiveValidators, pasoPrevio);
    if (!pasoPrevio) {
      this.f['NumPasosPrevios'].setValue('1', { emitEvent: false });
      this.f['NumPasosPreviosDiarios'].setValue('1', { emitEvent: false });
    }

    if (!value.CompDiaSemana) {
      this.f['DiasSemana'].setValue([], { emitEvent: false });
    }

    // COMENTADO TEMPORARIAMENTE - SERÁ IMPLEMENTADO NO FUTURO
    // this.panelCalendarioEnabled = !!value.CompCalendario;
    // if (!this.panelCalendarioEnabled) {
    //   this.calendarioRequiredError = false;
    // }
    // this.panelCalendarioHorarioEnabled = !!value.CompCalendHorario;
    // if (!this.panelCalendarioHorarioEnabled) {
    //   this.calendarioHorarioRequiredError = false;
    // }
    
    // Forçar como false enquanto não implementado
    this.panelCalendarioEnabled = false;
    this.panelCalendarioHorarioEnabled = false;
  }

  private setControlValidators(controlName: string, base: any[], required: boolean): void {
    const control = this.f[controlName];
    if (!control) {
      return;
    }
    const validators = required ? [Validators.required, ...base] : base;
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private hasIncompleteRow<T extends CalItemFecha | CalItemHora>(rows: T[]): boolean {
    return rows.some(row => !row.FechaAcceso || !row.FechaAccesoFin);
  }

  private validateFechas(rows: CalItemFecha[]): boolean {
    return rows.every(row => {
      if (!row.FechaAcceso || !row.FechaAccesoFin) {
        return false;
      }
      const desde = new Date(row.FechaAcceso);
      const hasta = new Date(row.FechaAccesoFin);
      return desde instanceof Date && !isNaN(desde.getTime()) && hasta instanceof Date && !isNaN(hasta.getTime()) && desde < hasta;
    });
  }

  private validateHoras(rows: CalItemHora[]): boolean {
    const toMinutes = (value: string): number => {
      const [h, m] = (value || '').split(':');
      const hours = parseInt(h, 10);
      const minutes = parseInt(m, 10);
      if (isNaN(hours) || isNaN(minutes)) {
        return NaN;
      }
      return hours * 60 + minutes;
    };

    return rows.every(row => {
      if (!row.FechaAcceso || !row.FechaAccesoFin) {
        return false;
      }
      const start = toMinutes(row.FechaAcceso);
      const end = toMinutes(row.FechaAccesoFin);
      return !isNaN(start) && !isNaN(end) && start < end;
    });
  }

  private readonly toDateInputFormat = (
    value: string | null | undefined
  ): string => {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (slashMatch) {
      const [, dd, mm, yyyy] = slashMatch;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
      const day = `${parsed.getDate()}`.padStart(2, '0');
      return `${parsed.getFullYear()}-${month}-${day}`;
    }
    return '';
  };

  private readonly fromDateInputFormat = (
    value: string | null | undefined
  ): string => {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [yyyy, mm, dd] = trimmed.split('-');
      return `${dd.padStart(2, '0')}/${mm.padStart(2, '0')}/${yyyy}`;
    }
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      const day = `${parsed.getDate()}`.padStart(2, '0');
      const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
      return `${day}/${month}/${parsed.getFullYear()}`;
    }
    return trimmed;
  };

  private readonly toTimeInputFormat = (
    value: string | null | undefined
  ): string => {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (/^\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed.substring(0, 5);
    }
    const parsed = new Date(`1970-01-01T${trimmed}`);
    if (!isNaN(parsed.getTime())) {
      const hours = `${parsed.getHours()}`.padStart(2, '0');
      const minutes = `${parsed.getMinutes()}`.padStart(2, '0');
      return `${hours}:${minutes}`;
    }
    return '';
  };

  private readonly fromTimeInputFormat = (
    value: string | null | undefined
  ): string => {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{2}:\d{2}$/.test(trimmed)) {
      return `${trimmed}:00`;
    }
    return trimmed;
  };
}

function toggleEnable(control: AbstractControl, enable: boolean): void {
  if (enable) {
    control.enable({ emitEvent: false });
  } else {
    control.disable({ emitEvent: false });
  }
  }

function truthy(value: any): boolean {
  if (typeof value === 'string') {
    return ['true', '1', 'si', 'sí', 'yes', 'y'].includes(value.trim().toLowerCase());
  }
  return !!value;
      }

function valueOrEmpty(value: any): string {
  return value === undefined || value === null ? '' : value.toString();
}

